// How often the brain asks — TODO T17 (30 Aug 2026), given its own file at T30 (1 Sep 2026).
//
// CLAUDE.md § Brain tier settles the mechanism: "polling not sockets — keeps the BFF stateless
// and out of the realtime lane (no warm capacity, no drain-on-deploy, no pinned sessions)". In
// TanStack Query that is `refetchInterval`.
//
// EVERY INTERVAL IN THE APP IS IN THIS ONE FILE, and it stays that way while the domain modules
// multiply (design doc § T30.8: "intervals stay in one api/intervals.ts"). They are a cost
// decision as much as a UX one — each one is a request per recruiter per interval against a
// service sized on seats — and a number spread across nine files is a number nobody can total.

/**
 * Chosen by how fast the underlying thing actually changes, not by what feels live.
 *
 * A thread moves at WhatsApp speed while a recruiter is reading it, so four seconds is the one
 * that matters; the poll is a delta (a cursor), so its cost does not grow with the conversation.
 * Searches and their candidate lists change when a human edits them, which is minutes apart.
 */
export const POLL_MS = {
  searches: 30_000,
  candidates: 30_000,
  conversations: 15_000,
  thread: 4_000,
  /**
   * T156 (28 Sep 2026): the demo candidate's thread while it is open, twice as often, so the room
   * sees her answers and kadmon's as they land (Natanel: "10 seconds max" for hers).
   */
  demoThread: 2_000,
  /** While catching up on a thread longer than one page — not a poll, a page turn. */
  threadCatchUp: 300,
  /**
   * Clients, tags, staff and codes — T18. A minute, because everything on these screens changes
   * only when a human here changes it, and a write refetches immediately anyway
   * (`useConsoleMutation`). The poll is for the other recruiter's edit, not for this one's.
   */
  slow: 60_000,
  /**
   * T62. A contact's page while kadmon is still writing one of its messages — a colleague whose page
   * polled mid-write, or a Try again. Three seconds for the few the model takes; the page drops
   * back to `slow` once every message on it is written.
   */
  draftWriting: 3_000,
  /**
   * T91. The Proposed list: a stage move asks kadmon about that person behind its own response,
   * so a row appears — and a `pending` one settles — with nobody on this screen having pressed
   * anything. The conversations' pace; a write here refetches at once.
   */
  proposals: 15_000,
  /**
   * T36. The activity rail sits on the home screen and the counts sit on every screen, so both
   * are one request per recruiter per half-minute for as long as a tab is open. Half a minute
   * because the feed is what kadmon's side recorded and a recruiter reads it, not races it; a
   * thread they are IN polls at four seconds, above.
   */
  activity: 30_000,
  counts: 30_000,
  /**
   * T32. Two minutes, the slowest poll in the app: every number on Reports is an aggregate over
   * seven to ninety days, so nothing on that screen moves visibly inside a minute, and each poll
   * is five aggregate queries rather than one list read.
   */
  reports: 120_000,
  /**
   * T55. The kadmon panel's chats and the open chat, while the panel is open and only then. Fifteen
   * seconds because a chat is SHARED: the other recruiter's question is the thing worth seeing,
   * and this recruiter's own lands the moment their ask returns (`useConsoleMutation` refetches).
   */
  chats: 15_000,
  /**
   * T56. A confirm card whose last step waits on the worker — a new person's record, which the
   * worker applies a second or two after the entry request is filed — polls the settle route at
   * this rate, only while it is waiting, and only in the tab that is looking at it. Three
   * seconds: the import's settle poll is the same wait for the same worker.
   */
  actionSettle: 3_000,
  /**
   * How long that poll keeps asking before the card says the rest will land on its own — the
   * import's own bound (api/import.ts `IMPORT_POLL_TIMEOUT_MS`). Not a timeout on the card: it
   * stays waiting, and opening the chat again restarts the poll.
   */
  actionSettleTimeout: 5 * 60_000,
  /**
   * 8 Sep 2026. How often a visible tab asks the CDN whether a newer brain is live
   * (app/NewVersion.tsx, the rules in src/version.ts). Against the bundle's own index.html, not
   * the service, so it sits outside the seat-scaled budget the rest of this file is — five
   * minutes, plus the moment a hidden tab is looked at again, which is the overnight case.
   */
  newVersion: 5 * 60_000,
  /**
   * T53. A research run while it is queued or running — a recruiter has just pressed Run and is
   * looking at the row — and the runs list while any run is moving. Five seconds: the service's
   * own sweep is five, and a run takes a minute or more. Done or failed, the read drops to `slow`.
   */
  researchRun: 5_000,
  /**
   * T53. An accepted proposal's settle poll: the worker applies the entry a second or two after
   * it is filed, and the service then puts the person on the Search. Three seconds, like the
   * import's and the confirm card's, for the same worker.
   */
  researchSettle: 3_000,
  /**
   * T53a. A contact lookup while it runs: its card asks the service to check with the provider,
   * which asks at most once in three seconds whoever polls. Four seconds; a lookup takes seconds to
   * a minute, and the poll stops on the answer.
   */
  contactLookup: 4_000,
} as const;

/** Things that do not change while a tab is open, and are refetched by the writes that move them. */
export const STALE_MS = {
  me: 5 * 60_000,
  staff: 5 * 60_000,
  tags: 60_000,
  /** T456a: kadmon's fit for an open Proactive row; a fact it learns moves it, a minute at most. */
  roleFit: 60_000,
} as const;
